import { useState, type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { useAuditEvents, useDiscovery, useHealth, useJwks } from '@/api/hooks';
import { ApiError } from '@/api/client';
import { MaterialIcon } from '@/components/MaterialIcon';
import { StatusBadge } from '@/components/StatusBadge';
import { IdentityFlow } from '@/components/IdentityFlow';
import { SkeletonRow } from '@/components/SkeletonRow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorCard } from '@/components/ErrorCard';
import { getAuditKindMeta } from '@/constants/audit-kinds';
import { SESSION_COOKIE } from '@/constants/session-cookie';
import { formatTimestamp } from '@/utils/format';
import { cn } from '@/utils/classnames';

/**
 * Screen 2 — visual structure mirrors docs/frontend/denox_identity_fabric_1/code.html.
 * Bento-grid layout: left 8/12 column with summary + cookie + discovery cards,
 * right 4/12 column with Identity Flow + recent audit timeline.
 *
 * Boundary rules from design.md:
 *   - Never invent "PostgreSQL: connected" / "Feishu OAuth: configured" /
 *     "Bootstrap Admin allowlist masked" cards — those subsystem health
 *     signals are not exposed by /healthz.
 *   - Audit timeline is captioned "前端聚合 · 按 occurred_at DESC".
 */
export function OverviewPage() {
  const health = useHealth();
  const jwks = useJwks();
  const discovery = useDiscovery();
  const audit = useAuditEvents(50);

  return (
    <div className="grid grid-cols-12 gap-panel-gap content-start">
      <div className="col-span-12 grid grid-cols-1 gap-panel-gap md:col-span-8 md:grid-cols-2">
        <ServiceStatusCard
          loading={health.isPending}
          error={health.error ?? null}
          service={health.data?.service ?? null}
          status={health.data?.status ?? null}
        />
        <IssuerCard loading={health.isPending} issuer={health.data?.issuer ?? null} />
        <JwksCard loading={jwks.isPending} keysCount={jwks.data?.keys.length ?? null} />
        <SessionCookieCard />
        <DiscoveryCard
          loading={discovery.isPending}
          error={discovery.error ?? null}
          fields={discovery.data ?? null}
        />
      </div>

      <div className="col-span-12 flex flex-col gap-panel-gap md:col-span-4">
        <IdentityFlowCard />
        <RecentAuditCard
          loading={audit.isPending}
          error={audit.error ?? null}
          events={audit.data ?? []}
        />
      </div>
    </div>
  );
}

// ─── Cards ─────────────────────────────────────────────────────────────────

const cardBase =
  'bg-surface border border-outline-variant rounded p-stack-md';

const cardCaptionRow =
  'mb-unit flex items-center gap-unit font-label-caps text-label-caps uppercase text-on-surface-variant';

const cardStagger = (index: number) => ({
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.22, delay: index * 0.05, ease: 'easeOut' as const },
});

function ServiceStatusCard({
  loading,
  error,
  service,
  status,
}: {
  loading: boolean;
  error: ApiError | null;
  service: string | null;
  status: string | null;
}) {
  const ok = status === 'ok';
  return (
    <motion.div
      {...cardStagger(0)}
      className={cn(cardBase, 'col-span-1 flex items-center justify-between md:col-span-2')}
    >
      <div className="min-w-0">
        <h2 className={cardCaptionRow}>Service Status</h2>
        {loading ? (
          <SkeletonRow cells={1} />
        ) : error ? (
          <span className="font-body-sm text-body-sm text-on-error-container">
            /healthz 请求失败：HTTP {error.status || '—'}
          </span>
        ) : (
          <div className="font-code-md text-code-md text-on-surface">{service ?? '—'}</div>
        )}
      </div>
      {!loading && !error ? (
        <StatusBadge variant={ok ? 'success' : 'warning'} dot>
          {ok ? '就绪 / RUNNING' : `状态：${status ?? 'unknown'}`}
        </StatusBadge>
      ) : null}
    </motion.div>
  );
}

function IssuerCard({ loading, issuer }: { loading: boolean; issuer: string | null }) {
  const display = issuer ?? '—';
  return (
    <motion.div {...cardStagger(1)} className={cn(cardBase, 'flex flex-col justify-between')}>
      <div>
        <h2 className={cardCaptionRow}>
          <MaterialIcon name="public" size={16} />
          Issuer
        </h2>
        {loading ? (
          <SkeletonRow cells={1} />
        ) : (
          <motion.a
            layoutId="issuer-pill"
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            href={display}
            target="_blank"
            rel="noreferrer"
            className="break-all font-code-md text-code-md text-primary hover:underline"
            title={display}
          >
            {display}
          </motion.a>
        )}
      </div>
    </motion.div>
  );
}

function JwksCard({ loading, keysCount }: { loading: boolean; keysCount: number | null }) {
  const hasKeys = (keysCount ?? 0) > 0;
  return (
    <motion.div {...cardStagger(2)} className={cn(cardBase, 'flex flex-col justify-between')}>
      <div>
        <h2 className={cardCaptionRow}>
          <MaterialIcon name="vpn_key" size={16} />
          JWKS
        </h2>
        <div className="mt-stack-sm flex items-center gap-stack-sm">
          {loading ? (
            <SkeletonRow cells={1} />
          ) : hasKeys ? (
            <>
              <StatusBadge variant="success" dot className="jwks-pulse">
                RS256 KEY LOADED
              </StatusBadge>
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                n={keysCount}
              </span>
            </>
          ) : (
            <>
              <StatusBadge variant="warning" dot>
                未加载签名密钥 / JWKS 为空
              </StatusBadge>
              <span className="font-body-sm text-body-sm text-on-surface-variant">n=0</span>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function SessionCookieCard() {
  return (
    <motion.div {...cardStagger(3)} className={cn(cardBase, 'col-span-1 md:col-span-2')}>
      <h2 className={cn(cardCaptionRow, 'mb-stack-sm')}>
        <MaterialIcon name="cookie" size={16} />
        Session Cookie
      </h2>
      <div className="flex flex-wrap gap-stack-sm">
        <span className="rounded border border-outline-variant bg-surface-container-low px-stack-sm py-unit font-code-md text-code-md text-on-surface">
          {SESSION_COOKIE.name}
        </span>
        {SESSION_COOKIE.httpOnly ? (
          <span className="flex items-center rounded bg-surface-container px-stack-sm py-unit font-label-caps text-label-caps uppercase text-on-surface">
            HttpOnly
          </span>
        ) : null}
        <span className="flex items-center rounded bg-surface-container px-stack-sm py-unit font-label-caps text-label-caps uppercase text-on-surface">
          SameSite={SESSION_COOKIE.sameSite}
        </span>
        {SESSION_COOKIE.secure ? (
          <span className="flex items-center gap-unit rounded bg-[#E3FCEF] px-stack-sm py-unit font-label-caps text-label-caps uppercase text-[#006644]">
            <MaterialIcon name="lock" size={14} />
            Secure
          </span>
        ) : null}
        <span className="flex items-center rounded bg-surface-container px-stack-sm py-unit font-label-caps text-label-caps uppercase text-on-surface">
          Path={SESSION_COOKIE.path}
        </span>
      </div>
      <p className="mt-stack-sm text-body-sm text-on-surface-variant">
        Cookie 由后端 /auth/feishu/callback 通过 Set-Cookie 写入；前端无法读取或主动清除。
      </p>
    </motion.div>
  );
}

const DISCOVERY_FIELDS: Array<[keyof DiscoveryFieldMap, string]> = [
  ['issuer', 'issuer'],
  ['authorization_endpoint', 'authorization_endpoint'],
  ['token_endpoint', 'token_endpoint'],
  ['userinfo_endpoint', 'userinfo_endpoint'],
  ['jwks_uri', 'jwks_uri'],
  ['response_types_supported', 'response_types_supported'],
  ['scopes_supported', 'scopes_supported'],
  ['id_token_signing_alg_values_supported', 'id_token_signing_alg_values_supported'],
  ['token_endpoint_auth_methods_supported', 'token_endpoint_auth_methods_supported'],
];

interface DiscoveryFieldMap {
  issuer: string;
  authorization_endpoint: string;
  token_endpoint: string;
  userinfo_endpoint: string;
  jwks_uri: string;
  response_types_supported: string[];
  scopes_supported: string[];
  id_token_signing_alg_values_supported: string[];
  token_endpoint_auth_methods_supported: string[];
}

function DiscoveryCard({
  loading,
  error,
  fields,
}: {
  loading: boolean;
  error: ApiError | null;
  fields: DiscoveryFieldMap | null;
}) {
  const [open, setOpen] = useState(true);
  return (
    <motion.div {...cardStagger(4)} className={cn(cardBase, 'col-span-1 md:col-span-2')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between"
        aria-expanded={open}
      >
        <h2 className={cn(cardCaptionRow, 'mb-0')}>
          <MaterialIcon name="find_in_page" size={16} />
          OIDC Discovery (.well-known)
        </h2>
        <MaterialIcon
          name="expand_more"
          size={20}
          className={cn('text-on-surface-variant transition-transform', open ? 'rotate-180' : '')}
        />
      </button>
      {open ? (
        <div className="mt-stack-md grid grid-cols-1 gap-x-stack-md gap-y-stack-sm border-t border-outline-variant pt-stack-md md:grid-cols-2">
          {loading ? (
            <SkeletonRow count={4} cells={2} className="col-span-full" />
          ) : error ? (
            <ErrorCard
              className="col-span-full"
              title="Discovery 请求失败"
              status={error.status}
              body={error.body}
            />
          ) : fields ? (
            DISCOVERY_FIELDS.map(([key, label]) => {
              const raw = fields[key];
              const value = Array.isArray(raw) ? raw.join(', ') : raw;
              return (
                <div key={String(key)}>
                  <div className="mb-unit font-label-caps text-label-caps uppercase text-on-surface-variant">
                    {label}
                  </div>
                  <div
                    className="break-words font-code-md text-code-md text-on-surface"
                    title={String(value)}
                  >
                    {value || '—'}
                  </div>
                </div>
              );
            })
          ) : null}
        </div>
      ) : null}
    </motion.div>
  );
}

function IdentityFlowCard() {
  return (
    <motion.div {...cardStagger(5)} className={cardBase}>
      <h2 className={cn(cardCaptionRow, 'mb-stack-md')}>
        <MaterialIcon name="account_tree" size={16} />
        Identity Flow
      </h2>
      <IdentityFlow className="md:flex-col md:items-stretch" />
      <p className="mt-stack-sm font-body-sm text-body-sm text-on-surface-variant">
        Feishu Identity → Denox Identity Fabric (OIDC · Access · Audit) → Downstream applications
      </p>
    </motion.div>
  );
}

function RecentAuditCard({
  loading,
  error,
  events,
}: {
  loading: boolean;
  error: ApiError | null;
  events: ReturnType<typeof useAuditEvents>['data'];
}) {
  const list = events ?? [];
  return (
    <motion.div {...cardStagger(6)} className={cn(cardBase, 'flex-1')}>
      <div className="mb-stack-md flex items-center justify-between">
        <h2 className={cn(cardCaptionRow, 'mb-0')}>
          <MaterialIcon name="list_alt" size={16} />
          Recent Audit
        </h2>
        <span className="font-label-caps text-label-caps uppercase text-on-surface-variant">
          前端聚合 · 按 occurred_at DESC
        </span>
      </div>
      {loading ? (
        <SkeletonRow count={5} cells={3} />
      ) : error ? (
        <ErrorCard title="审计事件请求失败" status={error.status} body={error.body} />
      ) : list.length === 0 ? (
        <EmptyState
          icon="check_circle"
          title="暂无审计事件"
          description="后端尚未写入任何 audit 事件，或 limit 过小。"
        />
      ) : (
        <ul className="flex flex-col gap-0">
          {list.map((event, i) => {
            const meta = getAuditKindMeta(event.kind);
            const isLast = i === list.length - 1;
            const subjectLine: ReactNode = event.actor_subject ?? '—';
            return (
              <li key={event.id} className="relative flex gap-stack-md pb-stack-md pt-stack-sm">
                {isLast ? null : (
                  <div className="absolute bottom-0 left-[3px] top-stack-md w-[2px] bg-outline-variant" />
                )}
                <div className="relative z-10 mt-[2px]">
                  <div className={cn('h-2 w-2 rounded-full ring-2 ring-surface', meta.dotClass)} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="mb-unit flex items-start justify-between gap-stack-sm">
                    <span className="font-code-md text-code-md text-on-surface">{event.kind}</span>
                    <span className="shrink-0 font-body-sm text-body-sm text-on-surface-variant">
                      {formatTimestamp(event.occurred_at)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-stack-sm text-body-sm text-on-surface-variant">
                    <span className="truncate" title={String(subjectLine)}>
                      {subjectLine}
                    </span>
                    <StatusBadge
                      variant={event.outcome === 'success' ? 'success' : 'danger'}
                    >
                      {event.outcome}
                    </StatusBadge>
                    {event.application_id ? (
                      <span className="rounded bg-surface-container px-stack-sm py-unit font-code-md text-code-md text-on-surface">
                        {event.application_id}
                      </span>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </motion.div>
  );
}
